import { MCP_TOOL_STAKE_LEVELS } from "@app/lib/actions/constants";
import { UserQuestionSchema } from "@app/lib/actions/types";
import type {
  ClariCall,
  ClariCallDetails,
} from "@app/lib/api/actions/servers/clari_copilot/types";
import {
  ClariCallDetailsSchema,
  ClariCallSchema,
} from "@app/lib/api/actions/servers/clari_copilot/types";
import { CONNECTOR_PROVIDERS } from "@app/types/data_source";
import type { AllSupportedFileContentType } from "@app/types/files";
import { ALL_FILE_FORMATS } from "@app/types/files";
import {
  CUSTOM_RESOURCE_ALLOWED,
  INTERNAL_ALLOWED_ICONS,
} from "@app/types/resources_icon_names";
import { INTERNAL_MIME_TYPES } from "@dust-tt/client";
import type {
  CallToolResult,
  Notification,
} from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";

export type SingleResourceToolOutput<
  T extends {
    uri: string;
    mimeType?: string;
  },
> = {
  content: [{ type: "resource"; resource: T }];
};

export function isBlobResource(
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: {
    uri: string;
    blob: string;
    mimeType?: string;
  };
} {
  return (
    outputBlock.type === "resource" &&
    "blob" in outputBlock.resource &&
    "uri" in outputBlock.resource
  );
}

// File generated by the tool during its execution.

const ToolGeneratedFileSchema = z.object({
  text: z.string(),
  uri: z.string(),
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.FILE),
  fileId: z.string(),
  title: z.string(),
  contentType: z.enum(
    Object.keys(ALL_FILE_FORMATS) as [
      AllSupportedFileContentType,
      ...AllSupportedFileContentType[],
    ]
  ),
  snippet: z.string().nullable(),
  isInProjectContext: z.boolean().optional(),
  // DEPRECATED, only kept for backwards compatibility. Optional UI hint to hide file from generic generated files sections.
  hidden: z.boolean().optional(),
});

export const TOOL_GENERATED_FILE_MIME_TYPE =
  INTERNAL_MIME_TYPES.TOOL_OUTPUT.FILE;
export const TOOL_GENERATED_FILE_PATH_MIME_TYPE =
  INTERNAL_MIME_TYPES.TOOL_OUTPUT.FILE_PATH;

export type ToolGeneratedFileType = z.infer<typeof ToolGeneratedFileSchema>;

export function isToolGeneratedFile(
  outputBlock: CallToolResult["content"][number] | null
): outputBlock is { type: "resource"; resource: ToolGeneratedFileType } {
  return (
    !!outputBlock &&
    outputBlock.type === "resource" &&
    ToolGeneratedFileSchema.safeParse(outputBlock.resource).success
  );
}

// File path only, no FileResource in DB.

const ToolGeneratedFilePathSchema = z.object({
  text: z.string(),
  uri: z.string(),
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.FILE_PATH),
  path: z.string(),
  title: z.string(),
  contentType: z.string(),
});

export type ToolGeneratedFilePathType = z.infer<
  typeof ToolGeneratedFilePathSchema
>;

export function isToolGeneratedFilePath(
  outputBlock: CallToolResult["content"][number] | null
): outputBlock is { type: "resource"; resource: ToolGeneratedFilePathType } {
  return (
    !!outputBlock &&
    outputBlock.type === "resource" &&
    ToolGeneratedFilePathSchema.safeParse(outputBlock.resource).success
  );
}

// File-system image returned by the `files__cat` tool for vision-capable models.
// Carries a path so the rendering layer can generate a signed URL at render time.
//
// Field evolution:
//   - v1 (legacy): `gcsPath` held a raw GCS object name (e.g. `w/{wId}/conversations/...`).
//     The rendering layer detects these by checking whether the value starts with `w/`.
//   - v2 (current): `filePath` holds a canonical scoped path (e.g. `conversation-{cId}/photo.png`).
//     Resolved via DustFileSystem.fromScopedPath at render time.
//
// The schema accepts both shapes so old stored records continue to parse.

const ModelVisionImageSchema = z
  .object({
    uri: z.string(),
    mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.MODEL_VISION_IMAGE),
    text: z.literal(""),
    imageContentType: z.string(),
  })
  .and(
    z
      .union([
        z.object({ filePath: z.string() }),
        z.object({ gcsPath: z.string() }).transform(({ gcsPath, ...rest }) => ({
          ...rest,
          filePath: gcsPath,
        })),
      ])
      .transform((v) => v)
  );

type ModelVisionImageType = z.infer<typeof ModelVisionImageSchema>;

export function isModelVisionImage(
  outputBlock: CallToolResult["content"][number] | null
): outputBlock is { type: "resource"; resource: ModelVisionImageType } {
  return (
    !!outputBlock &&
    outputBlock.type === "resource" &&
    ModelVisionImageSchema.safeParse(outputBlock.resource).success
  );
}

// Thinking tokens generated during the tool execution.

const ThinkingOutputSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.THINKING),
  text: z.string(),
  uri: z.literal(""),
});

export type ThinkingOutputType = z.infer<typeof ThinkingOutputSchema>;

export function isThinkingOutput(
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: ThinkingOutputType } {
  return (
    outputBlock.type === "resource" &&
    ThinkingOutputSchema.safeParse(outputBlock.resource).success
  );
}

// Final output of the reasoning when successful with the non-CoT tokens.

// SQL query generated during the tool execution.

const SqlQueryOutputSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.SQL_QUERY),
  text: z.string(),
  uri: z.literal(""),
});

export type SqlQueryOutputType = z.infer<typeof SqlQueryOutputSchema>;

export function isSqlQueryOutput(
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: SqlQueryOutputType } {
  return (
    outputBlock.type === "resource" &&
    SqlQueryOutputSchema.safeParse(outputBlock.resource).success
  );
}

// Resource with a name.

type ResourceWithName = {
  name: string;
};

export const isResourceWithName = (
  resource: object
): resource is ResourceWithName => {
  return "name" in resource && typeof resource.name === "string";
};

export const DatabaseSchemaResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATABASE_SCHEMA),
  text: z.string(),
  uri: z.string(),
});

export type DatabaseSchemaResourceType = z.infer<
  typeof DatabaseSchemaResourceSchema
>;

export const isDatabaseSchemaResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: DatabaseSchemaResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    DatabaseSchemaResourceSchema.safeParse(outputBlock.resource).success
  );
};

export const QueryWritingInstructionsResourceSchema = z.object({
  mimeType: z.literal(
    INTERNAL_MIME_TYPES.TOOL_OUTPUT.QUERY_WRITING_INSTRUCTIONS
  ),
  text: z.string(),
  uri: z.string(),
});

export type QueryWritingInstructionsResourceType = z.infer<
  typeof QueryWritingInstructionsResourceSchema
>;

export const ExampleRowsResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.EXAMPLE_ROWS),
  text: z.string(),
  uri: z.string(),
});

export type ExampleRowsResourceType = z.infer<typeof ExampleRowsResourceSchema>;

export const isExampleRowsResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: ExampleRowsResourceType } => {
  return (
    outputBlock.type === "resource" &&
    ExampleRowsResourceSchema.safeParse(outputBlock.resource).success
  );
};

export const ToolMarkerResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.TOOL_MARKER),
  text: z.string(),
  uri: z.literal(""),
});

export type ToolMarkerResourceType = z.infer<typeof ToolMarkerResourceSchema>;

export function isToolMarkerResourceType(
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: ToolMarkerResourceType;
} {
  return (
    outputBlock.type === "resource" &&
    ToolMarkerResourceSchema.safeParse(outputBlock.resource).success
  );
}

export const GET_DATABASE_SCHEMA_MARKER = "get_database_schema_marker" as const;

export const EXECUTE_TABLES_QUERY_MARKER =
  "execute_tables_query_marker" as const;

const ExecuteTablesQueryErrorResourceSchema = z.object({
  mimeType: z.literal(
    INTERNAL_MIME_TYPES.TOOL_OUTPUT.EXECUTE_TABLES_QUERY_ERROR
  ),
  text: z.string(),
  uri: z.literal(""),
});

export type ExecuteTablesQueryErrorResourceType = z.infer<
  typeof ExecuteTablesQueryErrorResourceSchema
>;

export const isExecuteTablesQueryErrorResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: ExecuteTablesQueryErrorResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    ExecuteTablesQueryErrorResourceSchema.safeParse(outputBlock.resource)
      .success
  );
};

export const SearchResultResourceSchema = z.object({
  mimeType: z.literal(
    INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATA_SOURCE_SEARCH_RESULT
  ),
  uri: z.string(),
  text: z.string(),

  // Document metadata
  id: z.string(),
  tags: z.array(z.string()),
  ref: z.string(),
  chunks: z.array(z.string()),
  source: z.object({
    provider: z.string().optional(),
    data_source_id: z.string().optional(),
    data_source_view_id: z.string().optional(),
  }),
});

export type SearchResultResourceType = z.infer<
  typeof SearchResultResourceSchema
>;

export const isSearchResultResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: SearchResultResourceType } => {
  return (
    outputBlock.type === "resource" &&
    SearchResultResourceSchema.safeParse(outputBlock.resource).success
  );
};

export const WarningResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.WARNING),
  warningTitle: z.string(),
  text: z.string(),
  warningData: z.record(z.string(), z.unknown()).optional(),
  uri: z.literal(""),
});

export type WarningResourceType = z.infer<typeof WarningResourceSchema>;

export const isWarningResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: WarningResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    WarningResourceSchema.safeParse(outputBlock.resource).success
  );
};

export const IncludeResultResourceSchema = z.object({
  mimeType: z.literal(
    INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATA_SOURCE_INCLUDE_RESULT
  ),
  uri: z.string(),
  text: z.string(),

  // Document metadata
  id: z.string(),
  tags: z.array(z.string()),
  ref: z.string(),
  chunks: z.array(z.string()),
  source: z.object({
    mimeType: z.string().optional(),
    name: z.string(),
    provider: z.string().optional(),
  }),
});

export type IncludeResultResourceType = z.infer<
  typeof IncludeResultResourceSchema
>;

export const isIncludeResultResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: IncludeResultResourceType } => {
  return (
    outputBlock.type === "resource" &&
    IncludeResultResourceSchema.safeParse(outputBlock.resource).success
  );
};

// Websearch results.

export const WebsearchResultResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.WEBSEARCH_RESULT),
  title: z.string(),
  text: z.string(),
  uri: z.string(),
  reference: z.string(),
});

export type WebsearchResultResourceType = z.infer<
  typeof WebsearchResultResourceSchema
>;

export const isWebsearchResultResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: WebsearchResultResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    WebsearchResultResourceSchema.safeParse(outputBlock.resource).success
  );
};

// Browse results.

export const BrowseResultResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.BROWSE_RESULT),
  requestedUrl: z.string(),
  uri: z.string(), // Browsed url, might differ from the requested url
  text: z.string(),
  html: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  responseCode: z.string(),
  errorMessage: z.string().optional(),
});

export type BrowseResultResourceType = z.infer<
  typeof BrowseResultResourceSchema
>;

export const isBrowseResultResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: BrowseResultResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    BrowseResultResourceSchema.safeParse(outputBlock.resource).success
  );
};

// RunAgent results.

export const RunAgentQueryResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.RUN_AGENT_QUERY),
  text: z.string(),
  childAgentId: z.string(),
  uri: z.literal(""),
  conversationId: z.string().optional(),
  agentMessageId: z.string().optional(),
});

export type RunAgentQueryResourceType = z.infer<
  typeof RunAgentQueryResourceSchema
>;

export const isRunAgentQueryResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: RunAgentQueryResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    RunAgentQueryResourceSchema.safeParse(outputBlock.resource).success
  );
};

// Toolsets results.

export const ToolsetsResultResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.TOOLSET_LIST_RESULT),
  text: z.string(),
  uri: z.literal(""),
  id: z.string(),
  description: z.string(),
});

export type ToolsetsResultResourceType = z.infer<
  typeof ToolsetsResultResourceSchema
>;

export const isToolsetsResultResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: ToolsetsResultResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    ToolsetsResultResourceSchema.safeParse(outputBlock.resource).success
  );
};

export const RunAgentResultResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.RUN_AGENT_RESULT),
  conversationId: z.string(),
  text: z.string(),
  chainOfThought: z.string().optional(),
  uri: z.string(),
  refs: z
    .record(
      z.string(),
      z.object({
        description: z.string().optional(),
        href: z.string().optional(),
        title: z.string(),
        provider: z.string(),
        contentType: z.string(),
      })
    )
    .optional(),
});

export type RunAgentResultResourceType = z.infer<
  typeof RunAgentResultResourceSchema
>;

export const isRunAgentResultResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: RunAgentResultResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    RunAgentResultResourceSchema.safeParse(outputBlock.resource).success
  );
};

// Extract data outputs: query and results.

export const ExtractQueryResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.EXTRACT_QUERY),
  text: z.string(),
  uri: z.literal(""),
});

export type ExtractQueryResourceType = z.infer<
  typeof ExtractQueryResourceSchema
>;

export const isExtractQueryResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: ExtractQueryResourceType } => {
  return (
    outputBlock.type === "resource" &&
    ExtractQueryResourceSchema.safeParse(outputBlock.resource).success
  );
};

export const ExtractResultResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.EXTRACT_RESULT),
  uri: z.string(),
  text: z.string(),

  // File metadata
  path: z.string().optional(), // scoped DustFileSystem path (new records)
  fileId: z.string().optional(), // legacy FileResource sId (old records)
  title: z.string(),
  contentType: z.string(),
  snippet: z.string().nullable(),
});

export type ExtractResultResourceType = z.infer<
  typeof ExtractResultResourceSchema
>;

export const isExtractResultResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: ExtractResultResourceType } => {
  return (
    outputBlock.type === "resource" &&
    ExtractResultResourceSchema.safeParse(outputBlock.resource).success
  );
};

// Schema for a content node rendered into a model-friendly format.
const RenderedNodeSchema = z.object({
  nodeId: z.string(),
  title: z.string(),
  path: z.string(),
  parentTitle: z.string().nullable(),
  lastUpdatedAt: z.string(),
  sourceUrl: z.string().nullable(),
  mimeType: z.string(),
  hasChildren: z.boolean(),
  connectorProvider: z.enum(CONNECTOR_PROVIDERS).nullable(),
});

export type RenderedNodeType = z.infer<typeof RenderedNodeSchema>;

export const DataSourceNodeListSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATA_SOURCE_NODE_LIST),
  uri: z.literal(""),
  text: z.string(),

  data: z.array(RenderedNodeSchema),
  nextPageCursor: z.string().nullable(),
  resultCount: z.number(),
});

export type DataSourceNodeListType = z.infer<typeof DataSourceNodeListSchema>;

export const isDataSourceNodeListType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: DataSourceNodeListType } => {
  return (
    outputBlock.type === "resource" &&
    DataSourceNodeListSchema.safeParse(outputBlock.resource).success
  );
};

const RenderedWarehouseNodeSchema = z.object({
  nodeId: z.string(),
  // TODO(2026-02-22 aubin): remove optional after a few weeks.
  // Having an optional here makes the type retro-compatible for the front-end schema check.
  tableId: z.string().nullable().optional(),
  title: z.string(),
  parentTitle: z.string().nullable(),
  mimeType: z.string(),
  hasChildren: z.boolean(),
  connectorProvider: z.enum(CONNECTOR_PROVIDERS).nullable(),
  sourceUrl: z.undefined(),
  lastUpdatedAt: z.undefined(),
});
export type RenderedWarehouseNodeType = z.infer<
  typeof RenderedWarehouseNodeSchema
>;

export const WAREHOUSES_BROWSE_MIME_TYPE =
  "application/vnd.dust.tool-output.data-warehouses-browse";

const WarehousesBrowseSchema = z.object({
  mimeType: z.literal(WAREHOUSES_BROWSE_MIME_TYPE),
  uri: z.literal(""),
  text: z.string(),
  nodeId: z.string().nullable(),
  data: z.array(RenderedWarehouseNodeSchema),
  nextPageCursor: z.string().nullable(),
  resultCount: z.number(),
});

export type WarehousesBrowseType = z.infer<typeof WarehousesBrowseSchema>;

export const isWarehousesBrowseType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is { type: "resource"; resource: WarehousesBrowseType } => {
  return (
    outputBlock.type === "resource" &&
    WarehousesBrowseSchema.safeParse(outputBlock.resource).success
  );
};

export const DataSourceNodeContentSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATA_SOURCE_NODE_CONTENT),
  uri: z.string(),
  text: z.string(),
  metadata: RenderedNodeSchema,
  ref: z.string().optional(),
});

export type DataSourceNodeContentType = z.infer<
  typeof DataSourceNodeContentSchema
>;

export const isDataSourceNodeContentType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: DataSourceNodeContentType;
} => {
  return (
    outputBlock.type === "resource" &&
    DataSourceNodeContentSchema.safeParse(outputBlock.resource).success
  );
};

// Schema for a locate_in_tree path item.
const FilesystemPathItemSchema = z.object({
  nodeId: z.string(),
  title: z.string(),
  nodeType: z.enum(["document", "table", "folder"]),
  sourceUrl: z.string().nullable(),
  isCurrentNode: z.boolean(),
});

export const FilesystemPathSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.FILESYSTEM_PATH),
  uri: z.literal(""),
  text: z.string(),
  path: z.array(FilesystemPathItemSchema),
});

export type FilesystemPathType = z.infer<typeof FilesystemPathSchema>;

export const isFilesystemPathType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: FilesystemPathType;
} => {
  return (
    outputBlock.type === "resource" &&
    FilesystemPathSchema.safeParse(outputBlock.resource).success
  );
};

/**
 * Notification output types.
 */

// Image.

const NotificationImageContentSchema = z.object({
  type: z.literal("image"),
  mimeType: z.string(),
});

type ImageProgressOutput = z.infer<typeof NotificationImageContentSchema>;

export function isImageProgressOutput(
  output: ProgressNotificationOutput
): output is ImageProgressOutput {
  return output !== undefined && output.type === "image";
}

// Interactive content file.

const NotificationInteractiveContentFileContentSchema = z.object({
  type: z.literal("interactive_content_file"),
  fileId: z.string(),
  mimeType: z.string(),
  title: z.string(),
  updatedAt: z.string(),
  // When false, refresh an already-open Frame panel but do not steal focus from
  // another panel (e.g. file explorer). Omitted / true = open as today.
  autoOpen: z.boolean().optional(),
});

type InteractiveContentFileContentProgressOutput = z.infer<
  typeof NotificationInteractiveContentFileContentSchema
>;

export function isInteractiveContentFileContentOutput(
  output: ProgressNotificationOutput
): output is InteractiveContentFileContentProgressOutput {
  return output !== undefined && output.type === "interactive_content_file";
}

const NotificationSidePanelControlSchema = z.object({
  type: z.literal("side_panel_control"),
  panel: z.enum(["files"]),
  action: z.enum(["open", "close"]),
});

type SidePanelControlProgressOutput = z.infer<
  typeof NotificationSidePanelControlSchema
>;

export function isSidePanelControlOutput(
  output: ProgressNotificationOutput
): output is SidePanelControlProgressOutput {
  return output !== undefined && output.type === "side_panel_control";
}

const InternalAllowedIconSchema = z.enum([...INTERNAL_ALLOWED_ICONS]);

const CustomResourceIconSchema = z.enum([...CUSTOM_RESOURCE_ALLOWED]);

// Schema for the resource of a notification where the tool is asking for tool approval.
// This schema contains all the information that the MCP server runner
// needs to emit an event for tool approval.
const NotificationToolApproveBubbleUpContentSchema = z.object({
  type: z.literal("tool_approval_bubble_up"),
  configurationId: z.string(),
  conversationId: z.string(),
  messageId: z.string(),
  actionId: z.string(),
  inputs: z.record(z.unknown()),
  stake: z.enum(MCP_TOOL_STAKE_LEVELS).optional(),
  metadata: z.object({
    mcpServerName: z.string(),
    toolName: z.string(),
    agentName: z.string(),
    publisherName: z.string().optional(),
    icon: z
      .union([InternalAllowedIconSchema, CustomResourceIconSchema])
      .optional(),
  }),
});

const NotificationStoreResourceContentSchema = z.object({
  type: z.literal("store_resource"),
  contents: z.array(
    z.object({
      type: z.literal("resource"),
      resource: z
        .object({
          mimeType: z.string(),
          text: z.string(),
          uri: z.string(),
        })
        .passthrough(),
    }) // Allow additional properties
  ),
});

const NotificationTextContentSchema = z.object({
  type: z.literal("text"),
  text: z.string(),
});

const NotificationRunAgentContentSchema = z.object({
  type: z.literal("run_agent"),
  childAgentId: z.string(),
  conversationId: z.string(),
  query: z.string(),
  userMessageId: z.string(),
  agentMessageId: z.string().nullable(),
});

export type RunAgentQueryProgressOutput = z.infer<
  typeof NotificationRunAgentContentSchema
>;

export function isRunAgentQueryProgressOutput(
  output: ProgressNotificationOutput
): output is RunAgentQueryProgressOutput {
  return (
    output !== undefined &&
    output.type === "run_agent" &&
    "childAgentId" in output
  );
}

type StoreResourceProgressOutput = z.infer<
  typeof NotificationStoreResourceContentSchema
>;

export function isStoreResourceProgressOutput(
  output: ProgressNotificationOutput
): output is StoreResourceProgressOutput {
  return output !== undefined && output.type === "store_resource";
}

export const ProgressNotificationOutputSchema = z
  .union([
    NotificationImageContentSchema,
    NotificationInteractiveContentFileContentSchema,
    NotificationSidePanelControlSchema,
    NotificationRunAgentContentSchema,
    NotificationStoreResourceContentSchema,
    NotificationTextContentSchema,
    NotificationToolApproveBubbleUpContentSchema,
  ])
  .optional();

type ProgressNotificationOutput = z.infer<
  typeof ProgressNotificationOutputSchema
>;

export const ProgressNotificationContentSchema = z.object({
  // Required for the MCP protocol.
  progress: z.number(),
  total: z.number(),
  progressToken: z.union([z.string(), z.number()]),
  // Custom data.
  _meta: z.object({
    //progressToken: z.union([z.string(), z.number()]),
    data: z.object({
      label: z.string(),
      output: ProgressNotificationOutputSchema,
    }),
  }),
});

export type ProgressNotificationContentType = z.infer<
  typeof ProgressNotificationContentSchema
>;

export const MCPProgressNotificationSchema = z.object({
  method: z.literal("notifications/progress"),
  params: ProgressNotificationContentSchema,
});

export type MCPProgressNotificationType = z.infer<
  typeof MCPProgressNotificationSchema
>;

export function isMCPProgressNotificationType(
  notification: Notification
): notification is MCPProgressNotificationType {
  return MCPProgressNotificationSchema.safeParse(notification).success;
}

export function isTextContent(
  content: CallToolResult["content"][number]
): content is { type: "text"; text: string } {
  return content.type === "text";
}

export function isResourceContentWithText(
  content: CallToolResult["content"][number]
): content is {
  type: "resource";
  resource: {
    [x: string]: unknown;
    text: string;
    uri: string;
    mimeType?: string | undefined;
  };
} {
  return (
    content.type === "resource" &&
    typeof content.resource === "object" &&
    content.resource !== null &&
    "text" in content.resource &&
    typeof (content.resource as any).text === "string"
  );
}

export const getOutputText = (
  output: CallToolResult["content"][number]
): string => {
  if (isTextContent(output)) {
    return output.text;
  }
  if (isResourceContentWithText(output)) {
    return output.resource.text;
  }
  return "";
};

// Internal tool output.

export const AuthRequiredOutputResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z.literal("tool_personal_auth_required"),
  provider: z.string(),
  scope: z.string().optional(),
  text: z.string(),
  uri: z.string(),
});

export type AuthRequiredOutputResourceType = z.infer<
  typeof AuthRequiredOutputResourceSchema
>;

export const FileAuthRequiredOutputResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z.literal("tool_file_auth_required"),
  fileId: z.string(),
  fileName: z.string(),
  connectionId: z.string(),
  mimeType_file: z.string(),
  text: z.string(),
  uri: z.string(),
});

export type FileAuthRequiredOutputResourceType = z.infer<
  typeof FileAuthRequiredOutputResourceSchema
>;

export const BlockedAwaitingInputOutputResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z.literal("tool_blocked_awaiting_input"),
  text: z.string(),
  uri: z.string(),
  blockingEvents: z.array(z.any()),
  state: z.any(),
});

export type BlockedAwaitingInputOutputResourceType = z.infer<
  typeof BlockedAwaitingInputOutputResourceSchema
>;

export const EarlyExitOutputResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z.literal("tool_early_exit"),
  text: z.string(),
  isError: z.boolean(),
  reason: z
    .enum(["deploy_interruption", "user_cancellation", "none"])
    .optional(),
  uri: z.string(),
});

export type EarlyExitOutputResourceType = z.infer<
  typeof EarlyExitOutputResourceSchema
>;

export const UserAnswerRequiredOutputResourceSchema = z.object({
  mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z.literal("tool_user_answer_required"),
  question: UserQuestionSchema,
  text: z.string(),
  uri: z.string(),
});

export const AgentPauseOutputResourceSchema = z.union([
  AuthRequiredOutputResourceSchema,
  BlockedAwaitingInputOutputResourceSchema,
  EarlyExitOutputResourceSchema,
  FileAuthRequiredOutputResourceSchema,
  UserAnswerRequiredOutputResourceSchema,
]);

export type AgentPauseOutputResourceType = z.infer<
  typeof AgentPauseOutputResourceSchema
>;

export const isAgentPauseOutputResourceType = (
  outputBlock: CallToolResult["content"][number]
): outputBlock is {
  type: "resource";
  resource: AgentPauseOutputResourceType;
} => {
  return (
    outputBlock.type === "resource" &&
    AgentPauseOutputResourceSchema.safeParse(outputBlock.resource).success
  );
};

// Outlook mail folder list tool output.

export const OUTLOOK_MAIL_FOLDER_LIST_MIME_TYPE =
  "application/vnd.dust.tool-output.outlook-mail-folder-list" as const;

const OutlookFolderItemSchema = z.object({
  name: z.string(),
  childFolderCount: z.number().optional(),
  unreadItemCount: z.number().optional(),
  totalItemCount: z.number().optional(),
});

const OutlookMailFolderListResourceSchema = z.object({
  mimeType: z.literal(OUTLOOK_MAIL_FOLDER_LIST_MIME_TYPE),
  uri: z.literal(""),
  text: z.string(),
  path: z.array(z.string()),
  folders: z.array(OutlookFolderItemSchema),
});

export type OutlookMailFolderListResourceType = z.infer<
  typeof OutlookMailFolderListResourceSchema
>;

// Clari Copilot tool outputs.

export const CLARI_CALL_LIST_MIME_TYPE =
  "application/vnd.dust.tool-output.clari-call-list" as const;

const ClariCallListResourceSchema = z.object({
  mimeType: z.literal(CLARI_CALL_LIST_MIME_TYPE),
  uri: z.literal(""),
  text: z.string(),
  calls: z.array(ClariCallSchema),
  pagination: z
    .object({
      matched: z.number().optional(),
      hasMore: z.boolean().optional(),
      nextPageSkip: z.number().optional(),
    })
    .optional(),
});

export type ClariCallListResourceType = z.infer<
  typeof ClariCallListResourceSchema
> & {
  calls: ClariCall[];
};

export const CLARI_CALL_DETAILS_MIME_TYPE =
  "application/vnd.dust.tool-output.clari-call-details" as const;

const ClariCallDetailsResourceSchema = z.object({
  mimeType: z.literal(CLARI_CALL_DETAILS_MIME_TYPE),
  uri: z.string(),
  text: z.string(),
  call: ClariCallDetailsSchema,
});

export type ClariCallDetailsResourceType = z.infer<
  typeof ClariCallDetailsResourceSchema
> & { call: ClariCallDetails };
